import {
  GoogleAuthProvider,
  signInWithPopup,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
} from 'firebase/auth'

import {modularAuth, db, functions} from './firebase'

/**
 * Ensure the users/{uid} Firestore profile document exists.
 * Uses setDoc with merge:true so it never overwrites existing data.
 * Called explicitly after sign-in because the modular SDK bypasses
 * react-redux-firebase's built-in profile creation that only runs
 * via firebase.login().
 */
export async function ensureUserProfile(user) {
  const userRef = db.collection('users').doc(user.uid)
  const snapshot = await userRef.get()
  if (!snapshot.exists) {
    await userRef.set(
      {
        email: user.email,
        playerName: '',
      },
      {merge: true}
    )
  }
}

const EMAIL_STORAGE_KEY = 'emailForSignIn'

// --- Bare functions (accept auth as first param) — used directly in tests ---

export function isEmailLinkUrlWithAuth(auth, url) {
  return isSignInWithEmailLink(auth, url)
}

export function completeEmailLinkSignInWithAuth(auth, email, url) {
  return signInWithEmailLink(auth, email, url).then(async (result) => {
    window.localStorage.removeItem(EMAIL_STORAGE_KEY)
    // Ensure the Firestore profile document exists. The modular SDK bypasses
    // rrf's firebase.login() which normally handles profile creation.
    // Only do this when using production auth (modularAuth), not in tests.
    if (auth === modularAuth) {
      await ensureUserProfile(result.user)
    }
    return result
  })
}

// --- Bound helpers (use production auth — consumed by UI components) ---

export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider()
  const result = await signInWithPopup(modularAuth, provider)
  await ensureUserProfile(result.user)
  return result
}

export function sendEmailLink(email) {
  const sendLoginEmail = functions.httpsCallable('sendLoginEmail')
  return sendLoginEmail({email}).then(() => {
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email)
  })
}

export function isEmailLinkUrl(url) {
  return isEmailLinkUrlWithAuth(modularAuth, url)
}

export function completeEmailLinkSignIn(email, url) {
  return completeEmailLinkSignInWithAuth(modularAuth, email, url)
}

export function getStoredEmail() {
  return window.localStorage.getItem(EMAIL_STORAGE_KEY)
}

export function signOutUser() {
  return signOut(modularAuth)
}

// --- Finnish error messages ---

const DEFAULT_ERROR =
  'Kirjautuminen epäonnistui. Yritä uudelleen tai ota yhteyttä tukeen.'

const ERROR_MESSAGES = {
  'auth/invalid-action-code':
    'Linkki on virheellinen tai jo käytetty. Pyydä uusi kirjautumislinkki.',
  'auth/expired-action-code':
    'Linkki on vanhentunut. Pyydä uusi kirjautumislinkki.',
  'auth/invalid-email': 'Sähköpostiosoite on virheellinen.',
  'auth/user-disabled': 'Tili on poistettu käytöstä. Ota yhteyttä tukeen.',
  'auth/account-exists-with-different-credential':
    'Sähköpostiosoitteeseen on jo yhdistetty tili. Kirjaudu toisella menetelmällä.',
  'auth/popup-blocked':
    'Selain esti ponnahdusikkunan. Salli ponnahdusikkunat ja yritä uudelleen.',
  'auth/popup-closed-by-user': 'Kirjautuminen peruutettiin.',
  'auth/network-request-failed':
    'Verkkovirhe. Tarkista internetyhteytesi ja yritä uudelleen.',
  'auth/too-many-requests':
    'Liian monta yritystä. Odota hetki ja yritä uudelleen.',
  'auth/email-not-found': 'Sähköpostiosoitetta ei löydy.',
  'auth/wrong-password': 'Virheellinen salasana.',
  'auth/user-not-found': 'Käyttäjää ei löydy.',
  'functions/invalid-argument': 'Sähköpostiosoite on virheellinen.',
  'functions/failed-precondition':
    'Kirjautumislinkkiä ei voida lähettää juuri nyt. Yritä myöhemmin uudelleen.',
  'functions/internal': DEFAULT_ERROR,
}

export function mapAuthError(code) {
  return ERROR_MESSAGES[code] || DEFAULT_ERROR
}
